import { useMemo, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Card, Button, Badge } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import type { BadgeProps } from "@/design-system/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { MarkdownReader } from "@/components/reports/v32/MarkdownReader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import {
  usePoolIdup,
  usePoolIdupPreview,
  useRegenerateIdup,
  useUpdateIdupStatus,
} from "@/hooks/capitalMarkets/useSpvPools";
import type { IDUPStatus, SpvPoolDto } from "@/types/spvPool";
import { formatDate, humanizeStatus } from "../../shared/format";

const IDUP_STATUS_BADGE: Record<IDUPStatus, NonNullable<BadgeProps["status"]>> = {
  NotStarted: "draft",
  Drafting: "info",
  InternalReview: "review",
  Approved: "finalized",
  Distributed: "gold",
};

export function IDUPTab({ pool }: { pool: SpvPoolDto }) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canIdup = hasPermission("SpvPool.IDUP");

  const idupQuery = usePoolIdup(pool.id);
  const regenerate = useRegenerateIdup(pool.id);
  const updateStatus = useUpdateIdupStatus(pool.id);

  // SPV Pool v1.0 PR 7 — generation can fail upstream in LLM & Reports v3.2;
  // surface the message inline with a Retry affordance.
  const [genError, setGenError] = useState<string | null>(null);
  const [previewIdupId, setPreviewIdupId] = useState<string | undefined>();

  const versions = useMemo(
    () =>
      [...(idupQuery.data ?? [])].sort((a, b) => b.version - a.version),
    [idupQuery.data],
  );
  const latest = versions[0];

  const handleRegenerate = () => {
    setGenError(null);
    regenerate
      .mutateAsync()
      .then((d) => toast.success(`Generated IDUP v${d.version}.`))
      .catch((err) => {
        const message = extractErrorMessage(err);
        setGenError(message);
        toast.error(message);
      });
  };

  const handleStatus = (idupId: string, status: IDUPStatus) => {
    updateStatus
      .mutateAsync({ idupId, status })
      .then(() => toast.success(`IDUP moved to ${humanizeStatus(status)}.`))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <div className="flex items-start justify-between gap-space-3">
          <div>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
              Current IDUP
            </h3>
            {latest ? (
              <div className="flex items-center gap-space-3">
                <span className="font-display font-bold text-h2 text-onyx">
                  v{latest.version}
                </span>
                <Badge status={IDUP_STATUS_BADGE[latest.status]}>
                  {humanizeStatus(latest.status)}
                </Badge>
                <span className="text-caption text-gray-mid">
                  Created {formatDate(latest.createdAt)}
                </span>
              </div>
            ) : (
              <p className="text-small text-gray-mid italic m-0">
                No IDUP generated yet.
              </p>
            )}
          </div>
          {canIdup && (
            <Button
              variant="secondary"
              size="sm"
              loading={regenerate.isPending}
              disabled={regenerate.isPending}
              onClick={handleRegenerate}
            >
              <RefreshCw size={14} strokeWidth={1.5} aria-hidden />
              Regenerate IDUP from latest data
            </Button>
          )}
        </div>

        {regenerate.isPending && (
          <div
            className="flex items-center gap-space-2 mt-space-3 rounded-md p-space-3 text-caption bg-status-info/10 border border-status-info/30"
            role="status"
          >
            <Loader2
              size={14}
              className="shrink-0 animate-spin text-status-info"
              aria-hidden
            />
            <span>
              Generating IDUP… this may take ~30s. Pulling the pool&apos;s
              tranches, reserve fund, and composition into the document.
            </span>
          </div>
        )}

        {genError && !regenerate.isPending && (
          <div className="flex items-start gap-space-2 mt-space-3 rounded-md p-space-3 text-caption bg-status-error/10 border border-status-error/30">
            <AlertTriangle
              size={14}
              className="shrink-0 mt-[2px] text-status-error"
              aria-hidden
            />
            <div className="flex-1">
              <p className="m-0 mb-space-2">IDUP generation failed: {genError}</p>
              <Button variant="secondary" size="sm" onClick={handleRegenerate}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {canIdup && latest && (
          <div className="flex flex-wrap gap-space-2 mt-space-3 pt-space-3 border-t border-border-light">
            <Button
              variant="secondary"
              size="sm"
              disabled={
                latest.status === "InternalReview" ||
                latest.status === "Approved" ||
                latest.status === "Distributed"
              }
              loading={updateStatus.isPending}
              onClick={() => handleStatus(latest.id, "InternalReview")}
            >
              Send for legal review
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                latest.status === "Approved" || latest.status === "Distributed"
              }
              loading={updateStatus.isPending}
              onClick={() => handleStatus(latest.id, "Approved")}
            >
              Approve &amp; finalize
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
          Version History
        </h3>
        {idupQuery.isLoading ? (
          <p className="text-small text-gray-mid italic m-0">Loading…</p>
        ) : versions.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No IDUP versions yet.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Version</TH>
                <TH>Status</TH>
                <TH>Created</TH>
                <TH>Approved</TH>
                <TH>Distributed</TH>
                <TH>Document</TH>
              </TR>
            </THead>
            <TBody>
              {versions.map((v) => (
                <TR key={v.id}>
                  <TD>v{v.version}</TD>
                  <TD>
                    <Badge status={IDUP_STATUS_BADGE[v.status]}>
                      {humanizeStatus(v.status)}
                    </Badge>
                  </TD>
                  <TD>{formatDate(v.createdAt)}</TD>
                  <TD>{formatDate(v.approvedAt)}</TD>
                  <TD>{formatDate(v.distributedAt)}</TD>
                  <TD>
                    {v.generatedReportId ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-space-1 text-small text-gold hover:underline"
                        onClick={() => setPreviewIdupId(v.id)}
                      >
                        <ExternalLink size={12} aria-hidden /> View
                      </button>
                    ) : (
                      <span className="text-caption text-gray-mid italic">
                        —
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {previewIdupId && (
        <IDUPPreviewModal
          poolId={pool.id}
          idupId={previewIdupId}
          onClose={() => setPreviewIdupId(undefined)}
        />
      )}
    </div>
  );
}

function IDUPPreviewModal({
  poolId,
  idupId,
  onClose,
}: {
  poolId: string;
  idupId: string;
  onClose: () => void;
}) {
  const previewQuery = usePoolIdupPreview(poolId, idupId);
  return (
    <Modal open onClose={onClose} title="IDUP Document" size="lg">
      {previewQuery.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading document…</p>
      ) : previewQuery.isError ? (
        <p className="text-small text-status-error m-0">
          {extractErrorMessage(previewQuery.error)}
        </p>
      ) : previewQuery.data ? (
        <MarkdownReader markdown={previewQuery.data.content} />
      ) : null}
    </Modal>
  );
}
